import { type Ref, computed } from "vue";
import { useStore } from "vuex";

import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";

import { ONE_MINUTE_MS } from "@/helpers/constants";
import applicationsService, {
  type GetApplicationAttributesParams,
} from "@/services/modules/applications";

import type { IApplicationAttribute } from "@/models/applications";

const APPLICATION_QUERY_KEYS = {
  PHONE_NUMBERS: ["options", "checkout"],
  APPLICATION_ATTRIBUTES: "application-attributes",
} as const;

export const useBulkToggleOffersSharedMutation = () => {
  const store = useStore();

  type BulkToggleOffersSharedParams = Parameters<
    typeof applicationsService.bulkToggleShowOffers
  >;

  return useMutation({
    mutationFn: (payload: {
      dealId: BulkToggleOffersSharedParams[0];
      data: BulkToggleOffersSharedParams[1];
    }) =>
      applicationsService.bulkToggleShowOffers(payload.dealId, payload.data),
    onSuccess: () => {
      store.dispatch("applications/getOffers");
    },
  });
};

export const useApplicationPhoneNumbersQuery = (appId: Ref<string>) => {
  return useQuery({
    queryKey: [APPLICATION_QUERY_KEYS.PHONE_NUMBERS, appId],
    queryFn: () => applicationsService.getApplicationPhoneNumbers(appId.value),
    staleTime: ONE_MINUTE_MS,
    enabled: computed(() => !!appId.value),
  });
};

export const useApplicationAttributesQuery = (
  appId: Ref<string>,
  params: Ref<GetApplicationAttributesParams>
) => {
  return useQuery({
    queryKey: computed(() => [
      APPLICATION_QUERY_KEYS.APPLICATION_ATTRIBUTES,
      appId.value,
      params.value,
    ]),
    queryFn: () =>
      applicationsService.getApplicationAttributes(appId.value, params.value),
    staleTime: ONE_MINUTE_MS,
    enabled: computed(() => !!appId.value),
    placeholderData: (previousData) => previousData,
  });
};

export const useUpdateApplicationAttributeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IApplicationAttribute) =>
      applicationsService.updateApplicationAttributeValue(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          APPLICATION_QUERY_KEYS.APPLICATION_ATTRIBUTES,
          variables.application_id,
        ],
      });
    },
  });
};
